/**
 * List operations — car/cdr family, construction, search and equality.
 * AutoLISP details preserved: `member` returns the sublist from the match,
 * `assoc` returns the matched pair, `subst` replaces at all nesting levels,
 * `append`'s last argument may be an atom (dotted result).
 */

import { LispError } from '../errors';
import { NIL, T, car, cdr, mkInt, mkList, valuesEqual, type LispRuntimeApi, type LispValue } from '../values';
import { define, expectList } from './internal';

const NIL_LIST: Extract<LispValue, { k: 'list' }> = { k: 'list', items: [], dot: null };

function listOf(v: LispValue, fn: string): Extract<LispValue, { k: 'list' }> {
    return v.k === 'nil' ? NIL_LIST : expectList(v, fn);
}

/** nth-level cdr chain: 'a' → cadr, 'ad' → cddr ... (max 4 levels, AutoCAD set). */
function cxr(path: string, v: LispValue): LispValue {
    let cur = v;
    for (let i = path.length - 1; i >= 0; i--) {
        if (cur.k !== 'list') return NIL;
        cur = path[i] === 'a' ? car(cur) : cdr(cur);
    }
    return cur;
}

export function registerList(rt: LispRuntimeApi): void {
    define(rt, 'car', (args) => car(args[0] ?? NIL));
    define(rt, 'cdr', (args) => cdr(args[0] ?? NIL));
    define(rt, 'caar', (args) => cxr('aa', args[0] ?? NIL));
    define(rt, 'cadr', (args) => cxr('ad', args[0] ?? NIL));
    define(rt, 'cdar', (args) => cxr('da', args[0] ?? NIL));
    define(rt, 'cddr', (args) => cxr('dd', args[0] ?? NIL));
    define(rt, 'caddr', (args) => cxr('add', args[0] ?? NIL));
    define(rt, 'cdddr', (args) => cxr('ddd', args[0] ?? NIL));
    define(rt, 'cadddr', (args) => cxr('addd', args[0] ?? NIL));

    define(rt, 'cons', (args) => {
        const a = args[0] ?? NIL;
        const d = args[1] ?? NIL;
        if (d.k === 'list') return mkList([a, ...d.items], d.dot);
        if (d.k === 'nil') return mkList([a]);
        return mkList([a], d);
    });
    define(rt, 'list', (args) => mkList(args));
    define(rt, 'append', (args) => {
        if (args.length === 0) return NIL;
        const heads = args.slice(0, -1).map((a) => listOf(a, 'append'));
        const tail = args[args.length - 1];
        const items: LispValue[] = [];
        for (const l of heads) items.push(...l.items);
        if (tail.k === 'list') {
            items.push(...tail.items);
            return mkList(items, tail.dot);
        }
        if (tail.k === 'nil') return mkList(items);
        return mkList(items, tail);
    });
    define(rt, 'length', (args) => {
        const l = listOf(args[0] ?? NIL, 'length');
        if (l.dot) throw new LispError('bad argument type');
        return mkInt(l.items.length);
    });
    define(rt, 'nth', (args) => {
        const nArg = args[0] ?? NIL;
        if (nArg.k !== 'int' && nArg.k !== 'real') throw new LispError('nth: bad argument type');
        const l = listOf(args[1] ?? NIL, 'nth');
        return l.items[Math.trunc(nArg.v)] ?? NIL;
    });
    define(rt, 'member', (args) => {
        const x = args[0] ?? NIL;
        const l = listOf(args[1] ?? NIL, 'member');
        for (let i = 0; i < l.items.length; i++) {
            if (valuesEqual(x, l.items[i])) {
                return mkList(l.items.slice(i), l.dot);
            }
        }
        return NIL;
    });
    define(rt, 'reverse', (args) => {
        const l = listOf(args[0] ?? NIL, 'reverse');
        if (l.dot) throw new LispError('bad argument type');
        return mkList([...l.items].reverse());
    });
    define(rt, 'assoc', (args) => {
        const key = args[0] ?? NIL;
        const l = listOf(args[1] ?? NIL, 'assoc');
        for (const pair of l.items) {
            if (pair.k === 'list' && pair.items.length > 0 && valuesEqual(key, pair.items[0])) {
                return pair;
            }
        }
        return NIL;
    });
    define(rt, 'subst', (args) => {
        const newValue = args[0] ?? NIL;
        const oldValue = args[1] ?? NIL;
        const replace = (v: LispValue): LispValue => {
            if (valuesEqual(v, oldValue)) return newValue;
            if (v.k === 'list') return mkList(v.items.map(replace), v.dot ? replace(v.dot) : null);
            return v;
        };
        return replace(args[2] ?? NIL);
    });
    define(rt, 'last', (args) => {
        const l = listOf(args[0] ?? NIL, 'last');
        return l.items.length > 0 ? l.items[l.items.length - 1] : NIL;
    });
    define(rt, 'eq', (args) => {
        const a = args[0] ?? NIL;
        const b = args[1] ?? NIL;
        if (a === b) return T;
        if (a.k === 'int' && b.k === 'int') return a.v === b.v ? T : NIL;
        if (a.k === 'real' && b.k === 'real') return a.v === b.v ? T : NIL;
        if (a.k === 'sym' && b.k === 'sym') return a.name === b.name ? T : NIL;
        if (a.k === 'ename' && b.k === 'ename') return a.id === b.id ? T : NIL;
        return NIL;
    });
    define(rt, 'equal', (args) => {
        const fuzzArg = args[2];
        const fuzz =
            fuzzArg && (fuzzArg.k === 'int' || fuzzArg.k === 'real') ? Math.abs(fuzzArg.v) : undefined;
        return valuesEqual(args[0] ?? NIL, args[1] ?? NIL, fuzz ?? 1e-8) ? T : NIL;
    });
    define(rt, 'atom', (args) => {
        const v = args[0] ?? NIL;
        return v.k === 'list' ? NIL : T;
    });
    define(rt, 'null', (args) => ((args[0] ?? NIL).k === 'nil' ? T : NIL));
    define(rt, 'not', (args) => ((args[0] ?? NIL).k === 'nil' ? T : NIL));
    define(rt, 'listp', (args) => {
        const v = args[0] ?? NIL;
        return v.k === 'list' || v.k === 'nil' ? T : NIL;
    });
    define(rt, 'consp', (args) => {
        const v = args[0] ?? NIL;
        return v.k === 'list' && (v.items.length > 0 || !!v.dot) ? T : NIL;
    });
    define(rt, 'numberp', (args) => {
        const v = args[0] ?? NIL;
        return v.k === 'int' || v.k === 'real' ? T : NIL;
    });
    define(rt, 'stringp', (args) => ((args[0] ?? NIL).k === 'str' ? T : NIL));
    define(rt, 'zerop', (args) => {
        const v = args[0] ?? NIL;
        return (v.k === 'int' || v.k === 'real') && v.v === 0 ? T : NIL;
    });
    define(rt, 'minusp', (args) => {
        const v = args[0] ?? NIL;
        return (v.k === 'int' || v.k === 'real') && v.v < 0 ? T : NIL;
    });
    define(rt, 'onep', (args) => {
        const v = args[0] ?? NIL;
        return (v.k === 'int' || v.k === 'real') && v.v === 1 ? T : NIL;
    });
}
